import { Component, Element, Prop, State, h } from '@stencil/core';
import Bind from 'bind-decorator';
import { createMutationObserver } from '../../utils/mutation-observer';

@Component({
	shadow: true,
	styleUrl: 'zds-breadcrumbs.scss',
	tag: 'zds-breadcrumbs',
})
export class ZdsBreadcrumbs {
	mutationObserver: MutationObserver;

	@Element() hostElement: HTMLElement;

	@Prop() separatorIcon: string = 'chevron-right';
	@Prop() maxItems: number;

	@State() isExpanded: boolean = false;

	componentDidUpdate() {
		this.applyBreadcrumbVisibility();
	}

	componentWillLoad() {
		this.applyBreadcrumbVisibility();

		this.mutationObserver = createMutationObserver(this.hostElement, this.handleMutation, {
			childList: true,
		});
	}

	disconnectedCallback() {
		this.mutationObserver?.disconnect();
	}

	render() {
		return (
			<nav aria-label="Breadcrumb">
				<div class="zds-breadcrumbs">
					<slot />
				</div>
			</nav>
		);
	}

	applyBreadcrumbVisibility() {
		// clean up any `meta` breadcrumbs that have been added by a previous render / mutation
		this.hostElement.querySelectorAll('[data-internally-rendered]').forEach((element) => element.remove());

		const breadcrumbs = this.hostElement.querySelectorAll('zds-breadcrumb');
		const maxItems = this.isExpanded ? breadcrumbs.length : this.maxItems || breadcrumbs.length;

		this.hostElement.querySelectorAll('zds-breadcrumb').forEach((breadcrumb, index) => {
			const isInvisibleBreadcrumb = index !== 0 && index <= breadcrumbs.length - maxItems;
			const isFirstInvisibleBreadcrumb = isInvisibleBreadcrumb && index === 1;

			breadcrumb.classList.add('zds-breadcrumbs__crumb');
			breadcrumb.classList.toggle('zds-breadcrumbs__crumb--invisible', isInvisibleBreadcrumb);
			breadcrumb.setAttribute('title', breadcrumb.innerText);

			// Do not show separators for invisible breadcrumbs (unless it's the first invisible breadcrumb, as we show an ellipsis there)
			(!isInvisibleBreadcrumb || isFirstInvisibleBreadcrumb) &&
				// Also, do not show a separator for the last breadcrumb
				index !== breadcrumbs.length - 1 &&
				breadcrumb.insertAdjacentHTML(
					'afterend',
					`<zds-icon class="zds-breadcrumbs__separator" library="fa-solid" name="${this.separatorIcon}" size="xxs" data-internally-rendered></zds-icon>`,
				);

			if (isFirstInvisibleBreadcrumb) {
				breadcrumb.insertAdjacentHTML(
					'afterend',
					// Added link attribute for styling, but disabled any default link behavior
					`<zds-breadcrumb link="javascript:{}" class="zds-breadcrumbs__crumb" data-internally-rendered ellipsis>...</zds-breadcrumb>`,
				);
				this.hostElement.querySelector('[ellipsis]').addEventListener('zdsClick', this.onEllipsisClick);
			}
		});
	}

	@Bind
	handleMutation() {
		// Let's not get into an infinite loop. We're temporarily disconnecting the mutation observer, as we'll be
		//	mutating the DOM within the mutator
		this.mutationObserver?.disconnect();

		this.applyBreadcrumbVisibility();

		// Mutation complete, re-observe
		requestAnimationFrame(() => {
			this.mutationObserver.observe(this.hostElement, {
				childList: true,
			});
		});
	}

	@Bind
	onEllipsisClick() {
		this.isExpanded = true;
	}
}
